import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/assets';
import { getTasks, reportTask, claimTaskReward, Task as ApiTask } from '../../api/tasks';
import { getUserSocials, bindSocialAccount, SocialAccount } from '../../api/user';
import { socialLogin } from '../../utils/socialAuth';
import { useAppSelector } from '../../app/hooks';
import { selectUserInfo } from '../../data/accountSlice';
import Loading from './Loading';
import AdModal from './AdModal';
import './ChanceModal.css';

// Extend or map API task to local interface if needed, but we can try to use API interface directly
// or adapt local usage.
// The local interface had:
// id: string; category, type, icon, desc, reward, btnText, status, currentCount, maxCount, inviteUrl
// API has:
// taskId: number, id: string, category, type, icon, desc, reward, btnText, status, currentProgress, targetProgress, jumpUrl

interface Task extends ApiTask {
  // Add any UI specific fields if necessary
}

interface ChanceModalProps {
  visible: boolean;
  onClose: () => void;
}

const ChanceModal: React.FC<ChanceModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation(['game']);
  const userInfo = useAppSelector(selectUserInfo);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [socials, setSocials] = useState<SocialAccount[]>([]);
  const [showAdModal, setShowAdModal] = useState(false);
  const [activeAdTaskId, setActiveAdTaskId] = useState<number | null>(null);
  const [activeAdPlacementId, setActiveAdPlacementId] = useState<string | undefined>(undefined);

  // Helper to get image path (same as GameplayScreen)
  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;
  
  // Helper for inner images - check if it's a full URL or a local asset name
  const getImgSrc = (name: string) => {
    if (name.startsWith('http') || name.startsWith('//')) {
      return name;
    }
    return getImageUrl('gameplay/' + name);
  };

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [visible]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tasksData, socialsData] = await Promise.all([getTasks(), getUserSocials()]);
      setTasks(tasksData.list);
      setSocials(socialsData);
    } catch (error) {
      console.error('Failed to fetch data', error);
      toast.error(t('common.loadingError', 'Failed to load tasks'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const data = await getTasks();
      setTasks(data.list);
    } catch (error) {
      console.error('Failed to fetch tasks', error);
    }
  };

  const handleTaskAction = async (taskId: number, taskStrId: string) => {
    const task = tasks.find(t => t.taskId === taskId);
    if (!task) return;

    if (task.type === 'ad') {
       setActiveAdTaskId(taskId);
       setActiveAdPlacementId(task.relatedId);
       setShowAdModal(true);
       return;
    }

    if (task.status === 'claimed') return;

    // If status is completed, we claim the reward
    if (task.status === 'completed') {
      await handleClaim(taskId);
      return;
    }

    // Check if task is social and not bound
    if (task.type === 'social' && task.platform) {
       const isBound = socials.some(s => s.platform === task.platform);
       if (!isBound) {
         // Trigger binding logic
         await handleBindSocial(task.platform);
         return;
       }
    }

    // Otherwise, perform action
    if (task.type === 'invite') {
      // Construct invite URL: current origin + path + ?invitationCode=CODE
      let inviteUrl = window.location.origin + window.location.pathname;
      if (userInfo?.myInvitationCode) {
        inviteUrl += `?invitationCode=${userInfo.myInvitationCode}`;
      } else {
        // Fallback if no code available yet, or use task.jumpUrl if provided
        inviteUrl = task.jumpUrl || inviteUrl;
      }

      navigator.clipboard.writeText(inviteUrl).then(() => {
        toast.success(t('chance.copySuccess', 'Link copied!'));
      }).catch(err => {
        console.error('Failed to copy: ', err);
        toast.error(t('chance.copyFailed', 'Failed to copy'));
      });
      
      // For invite, we don't report manually, status updates on refresh
    } else {
      // ... (existing logic for other tasks)
    }
  };

  const handleBindSocial = async (platform: string) => {
    toast.loading(t('chance.binding', 'Binding...'));
    try {
      // Use real social login flow
      const token = await socialLogin(platform);
      
      console.log(`[SocialAuth] Got token/code for ${platform}`, token);
      
      // Get redirect URI from config to pass to backend
      // We need to import SOCIAL_CONFIG or access it via helper
      // Since we don't have direct access here, we can reconstruct it or import it.
      // Better to modify socialLogin to return it or import config.
      // Importing config directly:
      const { SOCIAL_CONFIG } = await import('../../config/social');
       const config = SOCIAL_CONFIG[platform as keyof typeof SOCIAL_CONFIG];
       
       // For Twitter PKCE, retrieve codeVerifier from sessionStorage
       let codeVerifier: string | undefined;
       if (platform === 'twitter') {
         codeVerifier = sessionStorage.getItem('twitter_code_verifier') || undefined;
       }

       // Call API
       await bindSocialAccount(platform, token, config?.redirectUri, codeVerifier);
       
       // Clean up code verifier
       if (platform === 'twitter') {
         sessionStorage.removeItem('twitter_code_verifier');
       }
       
       toast.dismiss();
      toast.success(t('chance.bindSuccess', 'Binding successful'));
      
      // Refresh socials
      const socialsData = await getUserSocials();
      setSocials(socialsData);
      
    } catch (error: any) {
      console.error('Bind failed', error);
      toast.dismiss();
      toast.error(error.message || t('chance.bindFailed', 'Binding failed'));
    }
  };

  const handleClaim = async (taskId: number) => {
    try {
      const reward = await claimTaskReward(taskId);
      toast.success(t('chance.claimSuccess', { reward }));
      fetchTasks();
    } catch (error: any) {
      console.error('Failed to claim reward', error);
      toast.error(error.message || t('chance.claimFailed', 'Claim failed'));
    }
  };

  const handleAdComplete = async () => {
    if (activeAdTaskId) {
      // Refresh tasks to update status (backend should have updated status based on ad record)
      fetchTasks();
    }
  };

  if (!visible && tasks.length === 0 && !loading) return null;

  const basicTasks = tasks.filter((t) => t.category === 'basic');
  const dailyTasks = tasks.filter((t) => t.category === 'daily');

  return (
    <>
      <div className={`popup-container ${visible ? 'visible' : ''}`}>
        <div className="popup-backdrop" style={{ backgroundImage: getImg('popup-backdrop.png') }} onClick={onClose} />

        <div className="popup-content-wrapper">
          <div className="popup-panel-box">
            {/* Background */}
            <div className="popup-panel-bg" style={{ backgroundImage: getImg('popup-panel-bg.png') }} />

            {/* Title */}
            <div className="ad-popup-title">{t('chance.title')}</div>

            {/* Close Button */}
            <div
              className="close-button"
              style={{ backgroundImage: getImg('close-button.png') }}
              onClick={onClose}
            />

            {/* Loading */}
            {loading && <Loading />}

            {/* Scroll Area */}
            {!loading && (
              <div className="task-scroll-area">
                {/* Basic Tasks */}
                {basicTasks.length > 0 && (
                  <>
                    <div className="task-category">
                      <span className="task-category-text">&lt;{t('chance.basicTasks')}&gt;</span>
                    </div>
                    <ul className="task-list">
                      {basicTasks.map((task) => {
                        const isSocial = task.type === 'social' && task.platform;
                        const isBound = isSocial ? socials.some(s => s.platform === task.platform) : true;
                        return (
                          <TaskItem key={task.taskId} task={task} getImg={getImg} getImgSrc={getImgSrc} onAction={handleTaskAction} t={t} isBound={isBound} />
                        );
                      })}
                    </ul>
                  </>
                )}

                {/* Daily Tasks */}
                {dailyTasks.length > 0 && (
                  <>
                    <div className="task-category" style={{ marginTop: '0.2rem' }}>
                      <span className="task-category-text">&lt;{t('chance.dailyTasks')}&gt;</span>
                    </div>
                    <ul className="task-list">
                      {dailyTasks.map((task) => {
                        const isSocial = task.type === 'social' && task.platform;
                        const isBound = isSocial ? socials.some(s => s.platform === task.platform) : true;
                        return (
                          <TaskItem key={task.taskId} task={task} getImg={getImg} getImgSrc={getImgSrc} onAction={handleTaskAction} t={t} isBound={isBound} />
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ad Modal */}
      <AdModal 
        visible={showAdModal} 
        placementId={activeAdPlacementId}
        onClose={() => setShowAdModal(false)} 
        onComplete={handleAdComplete} 
      />
    </>
  );
};

interface TaskItemProps {
  task: Task;
  getImg: (name: string) => string;
  getImgSrc: (name: string) => string;
  onAction: (id: number, strId: string) => void;
  t: (key: string, options?: any) => string;
  isBound?: boolean; // New prop
}

const TaskItem: React.FC<TaskItemProps> = ({ task, getImg, getImgSrc, onAction, t, isBound = true }) => {
  const showProgress = (task.targetProgress || 1) > 1;
  const isCompleted = task.status === 'completed';
  const isClaimed = task.status === 'claimed';
  const isAd = task.type === 'ad';
  const isSocial = task.type === 'social' && task.platform;

  let btnText = task.btnText;
  if (isAd) {
     btnText = t('chance.watchAd', '看广告');
  } else if (isCompleted) {
     btnText = t('common.claim', '领取');
  } else if (isClaimed) {
     btnText = t('chance.completed', '已完成');
  } else if (isSocial && !isBound) {
     btnText = t('chance.bind', '绑定');
  }

  return (
    <li className="task-item" style={{ backgroundImage: getImg('task-row-bg.png') }}>
      <div className="task-icon-wrapper" style={{ backgroundImage: getImg('checkbox-empty.png') }}>
        <img src={getImgSrc(task.icon)} className="task-app-icon-inner" alt={task.desc} />
      </div>
      <div className="task-desc">{task.desc}</div>
      
      {/* Show progress if applicable */}
      {showProgress && (
        <div style={{ color: '#fff', fontSize: '0.2rem', marginRight: '0.1rem', fontWeight: 'bold' }}>
          ({task.currentProgress}/{task.targetProgress})
        </div>
      )}

      <div className="task-reward">
        <span className="reward-val">+{task.reward}</span>
        <div className="reward-icon" style={{ backgroundImage: getImg('reward-icon.png') }} />
      </div>
      <div
        className="task-btn"
        style={{ 
          backgroundImage: getImg('share-button.png'),
          opacity: isClaimed && !isAd ? 0.7 : 1,
          filter: isClaimed && !isAd ? 'grayscale(100%)' : 'none',
          cursor: isClaimed && !isAd ? 'default' : 'pointer'
        }}
        onClick={() => {
          // If claimed and NOT ad, prevent click
          if (isClaimed && !isAd) return;
          onAction(task.taskId, task.id);
        }}
      >
        <span>{btnText}</span>
      </div>
      <div className="task-divider" style={{ backgroundImage: getImg('divider-line.png') }} />
    </li>
  );
};

export default ChanceModal;
